import { getAllResultsPartialLoadQueryKey, getAllResultsPreloadStateQueryKey, getAllResultsQueryOptions, THREAD_METADATA_QUERY_KEY, } from '#pplx/frontend/apps/ask/all-results/hooks/allResultsQuery.js';
import { ALL_RESULTS_QUERY_KEY, BACKGROUND_ENTRIES_QUERY_KEY, FIRST_ENTRY_QUERY_KEY, LATEST_ENTRY_QUERY_KEY, PAGINATION_STATE_QUERY_KEY, getAllResultsQueryKey, getBackgroundEntriesQueryKey, getFirstEntryQueryKey, getLatestEntryQueryKey, getPaginationStateQueryKey, } from '#pplx/frontend/apps/ask/all-results/hooks/internal/threadPagination.js';
import { useThreadPagination } from '#pplx/frontend/apps/ask/all-results/hooks/internal/useThreadPagination.js';
import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import { BASE_SUPPORTED_BLOCK_USE_CASES } from '#pplx/frontend/components/utils/consts.js';
import { QueryClientHelper } from '#pplx/frontend/react-query/QueryClientHelper.js';
import { useQuery } from '#pplx/frontend/react-query/useQuery.js';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
/**
 * Fetches thread results for a given context UUID.
 *
 * Paginated callers receive the first visible page and directional pagination
 * controls. Complete-history callers eagerly drain pages. Both preserve the
 * same flat cache shape for stream updates and report later-page failures
 * through `isPartialLoad`.
 *
 * @param slugOrUUID - The slug or UUID of a result.
 * @param enabled - Whether to enable the query.
 */
export const useAllResultsQuery = ({ slugOrUUID, enabled, isSidecar, reason, enablePagination: paginationEnabled = false, paginateFromLatest = false, initialPaginatedFetchSize, preservePreviousData = true, supportedBlockUseCases: supportedBlockUseCasesOverride, timing, }) => {
    const queryClient = useQueryClient();
    const supportedBlockUseCases = supportedBlockUseCasesOverride ?? BASE_SUPPORTED_BLOCK_USE_CASES;
    const paginationPosition = paginateFromLatest ? 'start' : 'end';
    const activePaginationPosition = paginationEnabled ? paginationPosition : undefined;
    const queryKey = useMemo(() => getAllResultsQueryKey(slugOrUUID, activePaginationPosition), [activePaginationPosition, slugOrUUID]);
    const backgroundQueryKey = useMemo(() => getBackgroundEntriesQueryKey(slugOrUUID, activePaginationPosition), [activePaginationPosition, slugOrUUID]);
    const paginationStateQueryKey = useMemo(() => getPaginationStateQueryKey(slugOrUUID, paginationPosition), [paginationPosition, slugOrUUID]);
    const partialLoadQueryKey = useMemo(() => getAllResultsPartialLoadQueryKey(slugOrUUID, paginationEnabled, paginateFromLatest), [paginateFromLatest, paginationEnabled, slugOrUUID]);
    const preloadStateQueryKey = useMemo(() => getAllResultsPreloadStateQueryKey(slugOrUUID, paginationEnabled, paginateFromLatest), [paginateFromLatest, paginationEnabled, slugOrUUID]);
    const initialQueryTimingRef = useRef(undefined);
    const startInitialQueryTiming = useCallback((querySlugOrUUID) => {
        if (initialQueryTimingRef.current?.slugOrUUID === querySlugOrUUID) {
            return initialQueryTimingRef.current.startTime;
        }
        const startTime = performance.now();
        initialQueryTimingRef.current = { slugOrUUID: querySlugOrUUID, startTime };
        timing?.onThreadQueryStarted?.();
        return startTime;
    }, [timing]);
    const finishInitialQueryTiming = useCallback((querySlugOrUUID) => {
        const activeTiming = initialQueryTimingRef.current;
        if (activeTiming?.slugOrUUID !== querySlugOrUUID)
            return undefined;
        initialQueryTimingRef.current = undefined;
        return {
            durationMs: performance.now() - activeTiming.startTime,
            startTime: activeTiming.startTime,
        };
    }, []);
    const onPartialLoad = useCallback(() => {
        queryClient.setQueryData(partialLoadQueryKey, true);
    }, [partialLoadQueryKey, queryClient]);
    const onFirstPageResponse = useCallback((success) => {
        const measurement = finishInitialQueryTiming(slugOrUUID);
        if (!measurement)
            return;
        if (success)
            timing?.onThreadQueryResolved?.(measurement);
        else
            timing?.onThreadQueryFailed?.(measurement, 'response');
    }, [finishInitialQueryTiming, slugOrUUID, timing]);
    const queryResult = useQuery({
        ...getAllResultsQueryOptions({
            queryClient,
            slugOrUUID,
            paginationEnabled,
            paginateFromLatest,
            initialPaginatedFetchSize,
            supportedBlockUseCases,
            reason,
            isSidecar,
            lifecycle: {
                onFirstPageStarted: () => startInitialQueryTiming(slugOrUUID),
                onFirstPageResponse,
            },
        }),
        enabled: !!slugOrUUID && enabled,
        placeholderData: preservePreviousData ? keepPreviousData : undefined,
    });
    const { data: isPartialLoad = false } = useQuery({
        queryKey: partialLoadQueryKey,
        enabled: false,
        staleTime: Infinity,
    });
    const { data: preloadState } = useQuery({
        queryKey: preloadStateQueryKey,
        enabled: false,
        staleTime: Infinity,
    });
    const [consumedPreloadState, setConsumedPreloadState] = useState();
    const previousPreloadStateQueryKeyRef = useRef(preloadStateQueryKey);
    useLayoutEffect(() => {
        if (previousPreloadStateQueryKeyRef.current === preloadStateQueryKey)
            return;
        previousPreloadStateQueryKeyRef.current = preloadStateQueryKey;
        setConsumedPreloadState(undefined);
    }, [preloadStateQueryKey]);
    useLayoutEffect(() => {
        if (!preloadState)
            return;
        setConsumedPreloadState({
            queryKey: preloadStateQueryKey,
            startedAt: preloadState.startedAt,
            resolvedAt: preloadState.resolvedAt,
        });
        queryClient.removeQueries({ queryKey: preloadStateQueryKey, exact: true });
    }, [preloadState, preloadStateQueryKey, queryClient]);
    useEffect(() => {
        if (!queryResult.isError)
            return;
        const measurement = finishInitialQueryTiming(slugOrUUID);
        if (!measurement)
            return;
        timing?.onThreadQueryFailed?.(measurement, 'exception');
    }, [finishInitialQueryTiming, queryResult.isError, slugOrUUID, timing]);
    const { data: threadMetadata = null } = useQuery({
        queryKey: QueryClientHelper.makeQueryKey(THREAD_METADATA_QUERY_KEY, slugOrUUID),
        enabled: false,
        staleTime: Infinity,
    });
    const { data: backgroundEntries } = useQuery({
        queryKey: backgroundQueryKey,
        enabled: false,
        staleTime: Infinity,
    });
    const { data: latestEntry = null } = useQuery({
        queryKey: getLatestEntryQueryKey(slugOrUUID),
        enabled: false,
        staleTime: Infinity,
    });
    const { data: firstEntry = null } = useQuery({
        queryKey: getFirstEntryQueryKey(slugOrUUID),
        enabled: false,
        staleTime: Infinity,
    });
    const { data: paginationState } = useQuery({
        queryKey: paginationStateQueryKey,
        enabled: false,
        staleTime: Infinity,
    });
    const { isLoading: isLoadingMore, pagination } = useThreadPagination({
        backgroundQueryKey,
        enabled: paginationEnabled,
        onPartialLoad,
        paginateFromLatest,
        paginationState,
        paginationStateQueryKey,
        queryKey,
        reason,
        slugOrUUID,
        supportedBlockUseCases,
    });
    useEffect(() => {
        // Prefetch is bounded. Only a mounted, enabled query should keep looking for visible content.
        if (enabled &&
            !queryResult.isFetching &&
            !queryResult.isError &&
            queryResult.data?.length === 0 &&
            pagination?.hasMore &&
            !pagination.isLoading &&
            !pagination.hasError) {
            void pagination.loadMore();
        }
    }, [enabled, pagination, queryResult.data, queryResult.isError, queryResult.isFetching]);
    return {
        query: queryResult,
        isFetching: queryResult.isFetching || isLoadingMore,
        isPartialLoad,
        threadMetadata,
        firstEntry,
        latestEntry,
        backgroundEntries: backgroundEntries ?? EMPTY_ARRAY,
        pagination,
        preloadStartedAt: consumedPreloadState?.queryKey === preloadStateQueryKey
            ? consumedPreloadState.startedAt
            : preloadState?.startedAt,
        preloadResolvedAt: consumedPreloadState?.queryKey === preloadStateQueryKey
            ? consumedPreloadState.resolvedAt
            : preloadState?.resolvedAt,
    };
};
/**
 * Callback to set up to date state of results into react-query and
 * local storage.
 *
 * @returns (results: readonly PerplexityMessage[]) => void
 */
export const useUpdateResultsQuery = () => {
    const queryClient = useQueryClient();
    return useCallback((lastResponse, results) => {
        if (!results && !lastResponse)
            return;
        const identifiers = [
            lastResponse?.thread_url_slug || results?.at(0)?.thread_url_slug || '',
            lastResponse?.backend_uuid || results?.at(0)?.backend_uuid || '',
            lastResponse?.context_uuid || results?.at(0)?.context_uuid || '',
        ].filter(Boolean);
        // Update existing caches only so a partial page cannot seed a cache
        // used by a complete-history consumer.
        const queryKeys = identifiers
            .flatMap((identifier) => [
            getAllResultsQueryKey(identifier),
            getAllResultsQueryKey(identifier, 'start'),
            getAllResultsQueryKey(identifier, 'end'),
        ])
            .filter((queryKey) => queryClient.getQueryState(queryKey) != null);
        if (lastResponse) {
            identifiers.forEach((identifier) => {
                const latestEntryQueryKey = getLatestEntryQueryKey(identifier);
                if (queryClient.getQueryState(latestEntryQueryKey) != null) {
                    queryClient.setQueryData(latestEntryQueryKey, lastResponse);
                }
                const firstEntryQueryKey = getFirstEntryQueryKey(identifier);
                const currentFirstEntry = queryClient.getQueryData(firstEntryQueryKey);
                if (currentFirstEntry?.backend_uuid === lastResponse.backend_uuid) {
                    queryClient.setQueryData(firstEntryQueryKey, lastResponse);
                }
            });
        }
        queryKeys.forEach((queryKey) => {
            // If the query key is not a valid query key, nothing to do here
            if (!QueryClientHelper.unmakeQueryKey(queryKey).at(-1))
                return;
            if (lastResponse) {
                const existingResults = queryClient.getQueryData(queryKey) ?? [];
                const isInResults = results?.some((r) => r.backend_uuid === lastResponse?.backend_uuid);
                const index = existingResults.findIndex((result) => result.backend_uuid === lastResponse?.backend_uuid);
                if (index !== -1) {
                    // The last response is in the existing results and updated
                    const newResults = [...existingResults];
                    newResults[index] = lastResponse;
                    queryClient.setQueryData(queryKey, newResults);
                    return;
                }
                else if (isInResults && index === -1) {
                    // A new response is part of the thread but not yet in cache
                    const newResults = [...existingResults, lastResponse];
                    queryClient.setQueryData(queryKey, newResults);
                    return;
                }
            }
            if (results && !lastResponse) {
                const newResults = [...results];
                queryClient.setQueryData(queryKey, newResults);
                return;
            }
            // Something unexpected
            queryClient.invalidateQueries({ queryKey: queryKey });
        });
    }, [queryClient]);
};
/**
 * Callback to clear results from react-query and local storage.
 * If slugOrUUID is provided, it will clear the results for that
 * slugOrUUID, else all results will be cleared.
 *
 * @returns (slugOrUUID: string) => void
 */
export const useClearResultsQuery = () => {
    const queryClient = useQueryClient();
    return useCallback((slugOrUUID) => {
        if (slugOrUUID) {
            queryClient
                .getQueriesData({
                queryKey: QueryClientHelper.makeQueryKey(ALL_RESULTS_QUERY_KEY),
                exact: false,
            })
                ?.forEach(([queryKey, queryEntries]) => {
                if (!queryEntries)
                    return;
                const entries = queryEntries.filter((entry) => {
                    return entry.backend_uuid !== slugOrUUID && entry.thread_url_slug !== slugOrUUID;
                });
                if (entries.length !== queryEntries.length) {
                    if (entries.length)
                        queryClient.setQueryData(queryKey, entries);
                    else
                        queryClient.removeQueries({ queryKey, exact: true });
                }
            });
            queryClient.removeQueries({
                queryKey: QueryClientHelper.makeQueryKey(THREAD_METADATA_QUERY_KEY, slugOrUUID),
                exact: true,
            });
            queryClient.removeQueries({
                queryKey: QueryClientHelper.makeQueryKey(BACKGROUND_ENTRIES_QUERY_KEY, slugOrUUID),
                exact: false,
            });
            queryClient.removeQueries({
                queryKey: QueryClientHelper.makeQueryKey(PAGINATION_STATE_QUERY_KEY, slugOrUUID),
                exact: false,
            });
            queryClient.removeQueries({
                queryKey: getFirstEntryQueryKey(slugOrUUID),
                exact: true,
            });
            queryClient.removeQueries({
                queryKey: getLatestEntryQueryKey(slugOrUUID),
                exact: true,
            });
        }
        else {
            queryClient.removeQueries({
                queryKey: QueryClientHelper.makeQueryKey(ALL_RESULTS_QUERY_KEY),
                exact: false,
            });
            queryClient.removeQueries({
                queryKey: QueryClientHelper.makeQueryKey(THREAD_METADATA_QUERY_KEY),
                exact: false,
            });
            queryClient.removeQueries({
                queryKey: QueryClientHelper.makeQueryKey(BACKGROUND_ENTRIES_QUERY_KEY),
                exact: false,
            });
            queryClient.removeQueries({
                queryKey: QueryClientHelper.makeQueryKey(PAGINATION_STATE_QUERY_KEY),
                exact: false,
            });
            queryClient.removeQueries({
                queryKey: QueryClientHelper.makeQueryKey(FIRST_ENTRY_QUERY_KEY),
                exact: false,
            });
            queryClient.removeQueries({
                queryKey: QueryClientHelper.makeQueryKey(LATEST_ENTRY_QUERY_KEY),
                exact: false,
            });
        }
    }, [queryClient]);
};
//# sourceMappingURL=useAllResultsQuery.js.map